// src/utils/access.js

/**
 * Check if a user has access to the ACL Care program
 * @param {Object} user - The user object from auth context
 * @returns {boolean} - Whether the user has access
 */
export function hasAccess(user) {
  if (!user) return false;
  
  // Org-linked users always have access (for now)
  if (user.organization_id) return true;
  
  const aclCare = user.acl_care || {};
  
  const status = aclCare?.access_status;
  
  // Paid or access code = full access
  if (status === 'paid' || status === 'access_code') return true;
  
  // Trial = check expiration
  if (status === 'trial') {
    if (!aclCare?.trial_expires_at) return false;
    return new Date() < new Date(aclCare.trial_expires_at);
  }
  
  return false;
}

/**
 * Get the access status details for display
 * @param {Object} user - The user object from auth context
 * @returns {Object} - Access status details
 */
export function getAccessStatus(user) {
  if (!user) {
    return { hasAccess: false, status: 'none', message: 'Not logged in' };
  }
  
  // Org-linked users
  if (user.organization_id) {
    return { 
      hasAccess: true, 
      status: 'organization', 
      message: 'Access through organization' 
    };
  }
  
  const aclCare = user?.acl_care;
  if (!aclCare || !aclCare.access_status || aclCare.access_status === 'none') {
    return { 
      hasAccess: false, 
      status: 'none', 
      message: 'No access - payment required' 
    };
  }
  
  const status = aclCare?.access_status;
  
  if (status === 'paid') {
    return { 
      hasAccess: true, 
      status: 'paid', 
      message: 'Full access - paid' 
    };
  }
  
  if (status === 'access_code') {
    return { 
      hasAccess: true, 
      status: 'access_code', 
      message: 'Full access - access code' 
    };
  }
  
  if (status === 'trial') {
    const expiresAt = aclCare.trial_expires_at ? new Date(aclCare.trial_expires_at) : null;
    const now = new Date();
    
    if (expiresAt && now < expiresAt) {
      const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
      return { 
        hasAccess: true, 
        status: 'trial', 
        message: `Trial - ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`,
        daysLeft,
        expiresAt
      };
    } else {
      return { 
        hasAccess: false, 
        status: 'trial_expired', 
        message: 'Trial expired' 
      };
    }
  }
  
  if (status === 'trial_expired') {
    return { 
      hasAccess: false, 
      status: 'trial_expired', 
      message: 'Trial expired' 
    };
  }
  
  return { 
    hasAccess: false, 
    status: 'unknown', 
    message: 'Unknown access status' 
  };
}

/**
 * Check if user needs to see the payment step during onboarding
 * @param {Object} user - The user object from auth context
 * @returns {boolean} - Whether payment step is needed
 */
export function needsPaymentStep(user) {
  if (!user) return false;
  
  // Only patients need to pay
  const isPatient = user.patient_id !== null && user.patient_id !== undefined;
  if (!isPatient) return false;
  
  // Org-linked patients don't pay (for now)
  if (user.organization_id) return false;
  
  // Check if already has access
  return !hasAccess(user);
}